import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, Button, SectionCard, StatCard } from "@/components/PageUI";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Check, DollarSign, Plus, Bell, Landmark, CheckCircle2, Repeat, X, Mail, CreditCard, Link as LinkIcon, Undo2, Wallet, ArrowUpRight, ExternalLink } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { UpgradeGate } from "@/components/UpgradeGate";

const FEE_TYPES = ["Monthly Tuition", "Registration Fee", "Uniform Fee", "Tournament Fee", "Camp", "Private Lesson", "Donation"];

export default function Payments() {
  const { club, loadClub, isPremium } = useAuth();
  const [plans, setPlans] = useState([]);
  const [fees, setFees] = useState([]);
  const [txns, setTxns] = useState([]);
  const [players, setPlayers] = useState([]);
  const [feeOpen, setFeeOpen] = useState(false);
  const [feeForm, setFeeForm] = useState({ player_id: "", fee_type: FEE_TYPES[0], amount: "", due_date: "", description: "" });
  const [recurring, setRecurring] = useState(false);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [schedules, setSchedules] = useState([]);
  const [busy, setBusy] = useState("");
  const [connect, setConnect] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [tournaments, setTournaments] = useState([]);
  const [trnOpen, setTrnOpen] = useState(false);
  const [trnForm, setTrnForm] = useState({ name: "", date: "", location: "", description: "", fee: "" });
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState("all");
  const [storeOpen, setStoreOpen] = useState(false);
  const [prodForm, setProdForm] = useState({ name: "", description: "", price: "", image: "", sizes: "" });
  const [expandedTrn, setExpandedTrn] = useState("");
  const [trnRegs, setTrnRegs] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ player_ids: [], fee_type: FEE_TYPES[0], amount: "", due_date: "", description: "", send_links: true });
  const [feeFilter, setFeeFilter] = useState("all");
  const [refundFee, setRefundFee] = useState(null);
  const [refundAmt, setRefundAmt] = useState("");
  const [refunding, setRefunding] = useState(false);
  const [balance, setBalance] = useState(null);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [payoutAmt, setPayoutAmt] = useState("");
  const [payingOut, setPayingOut] = useState(false);
  const [openingDash, setOpeningDash] = useState(false);
  const [summary, setSummary] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [coverage, setCoverage] = useState([]);
  const hasCard = (id) => coverage.includes(id);

  const load = () => {
    api.get("/payments/plans").then((r) => setPlans(r.data.plans));
    api.get("/fees").then((r) => setFees(r.data));
    api.get("/payments/transactions").then((r) => setTxns(r.data));
    api.get("/players").then((r) => setPlayers(r.data));
    api.get("/fees/card-coverage").then((r) => setCoverage(r.data.covered)).catch(() => {});
    api.get("/connect/status").then((r) => setConnect(r.data)).catch(() => {});
    api.get("/connect/balance").then((r) => setBalance(r.data)).catch(() => {});
    api.get("/connect/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/connect/fee-breakdown").then((r) => setBreakdown(r.data)).catch(() => {});
    api.get("/fee-schedules").then((r) => setSchedules(r.data)).catch(() => {});
    api.get("/tournaments").then((r) => setTournaments(r.data)).catch(() => {});
    api.get("/store/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/store/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/payments/revenue").then((r) => setRevenue(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const onboardPayouts = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      const msg = apiErr(e);
      toast.error(/connect/i.test(msg) ? "Stripe Connect isn't enabled on the SphereHyx account yet. The platform owner must enable it once in the Stripe Dashboard (Connect → Get started), then try again." : msg);
      setConnecting(false);
    }
  };
  const openDashboard = async () => {
    setOpeningDash(true);
    try { const { data } = await api.post("/connect/dashboard"); window.open(data.url, "_blank", "noopener"); }
    catch (e) { toast.error(apiErr(e)); } finally { setOpeningDash(false); }
  };
  const openPayout = () => { setPayoutAmt((balance?.available || 0).toFixed(2)); setPayoutOpen(true); };
  const doPayout = async () => {
    const amt = parseFloat(payoutAmt);
    if (!amt || amt <= 0 || amt > (balance?.available || 0) + 0.001) return toast.error(`Enter an amount between ${cur} 0.01 and ${cur} ${(balance?.available || 0).toFixed(2)}`);
    setPayingOut(true);
    try {
      const { data } = await api.post("/connect/payout", { amount: amt });
      toast.success(`Cashing out ${data.currency} ${data.amount.toFixed(2)} to your bank${data.arrival_date ? ` — arriving ~${new Date(data.arrival_date * 1000).toLocaleDateString()}` : ""}`);
      setPayoutOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setPayingOut(false); }
  };

  const remind = async (id) => {
    try { await api.post(`/fees/${id}/remind`); toast.success("Reminder emailed to parent"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const subscribe = async (lookup_key) => {
    if (!lookup_key) return;
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setBusy(""); }
  };

  const createFee = async () => {
    if (!feeForm.player_id || !feeForm.amount) return toast.error("Athlete and amount required");
    try {
      if (recurring) {
        await api.post("/fee-schedules", { player_id: feeForm.player_id, fee_type: feeForm.fee_type, amount: +feeForm.amount, day_of_month: +dayOfMonth, description: feeForm.description });
        toast.success("Recurring monthly fee set up");
      } else {
        const { data } = await api.post("/fees", { ...feeForm, amount: +feeForm.amount });
        toast.success(data?.status === "paid" ? "Fee created & charged the saved card" : "Fee created");
      }
      setFeeOpen(false); setRecurring(false);
      setFeeForm({ player_id: "", fee_type: FEE_TYPES[0], amount: "", due_date: "", description: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const createBulkFees = async () => {
    if (!bulkForm.player_ids.length) return toast.error("Select at least one athlete");
    if (!bulkForm.amount) return toast.error("Amount is required");
    try {
      const { data } = await api.post("/fees/bulk", { ...bulkForm, amount: +bulkForm.amount });
      const parts = [`${data.created} fee${data.created === 1 ? "" : "s"} created`];
      if (data.charged) parts.push(`${data.charged} charged on file`);
      if (bulkForm.send_links && data.emailed) parts.push(`${data.emailed} link${data.emailed === 1 ? "" : "s"} emailed`);
      toast.success(parts.join(" · "));
      setBulkOpen(false);
      setBulkForm({ player_ids: [], fee_type: FEE_TYPES[0], amount: "", due_date: "", description: "", send_links: true });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const chargeNow = async (fee) => {
    if (!window.confirm(`Charge ${cur} ${fee.amount?.toFixed(2)} to ${playerName(fee.player_id)}'s saved card now?`)) return;
    try { const { data } = await api.post(`/fees/${fee.id}/charge`); toast.success(data.message || "Charged the card on file"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const chargeAllOverdue = async () => {
    if (!window.confirm("Charge every overdue fee that has a saved card on file now?")) return;
    try {
      const { data } = await api.post("/fees/charge-all-overdue");
      const parts = [`${data.charged} charged`];
      if (data.skipped) parts.push(`${data.skipped} no card`);
      if (data.failed) parts.push(`${data.failed} failed`);
      toast.success(parts.join(" · "));
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const toggleBulkPlayer = (id) => setBulkForm((f) => ({ ...f, player_ids: f.player_ids.includes(id) ? f.player_ids.filter((x) => x !== id) : [...f.player_ids, id] }));
  const copyPayLink = (fee) => { navigator.clipboard.writeText(`${window.location.origin}/pay/${fee.id}`); toast.success("Payment link copied"); };
  const openRefund = (fee) => { setRefundFee(fee); setRefundAmt(((fee.amount || 0) - (fee.refunded_amount || 0)).toFixed(2)); };
  const doRefund = async () => {
    const amt = parseFloat(refundAmt);
    const remaining = (refundFee.amount || 0) - (refundFee.refunded_amount || 0);
    if (!amt || amt <= 0 || amt > remaining + 0.001) return toast.error(`Enter an amount between ${cur} 0.01 and ${cur} ${remaining.toFixed(2)}`);
    setRefunding(true);
    try {
      const { data } = await api.post(`/fees/${refundFee.id}/refund`, { amount: amt });
      toast.success(`Refunded ${cur} ${data.refunded.toFixed(2)}${data.full ? "" : " (partial)"}`);
      setRefundFee(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setRefunding(false); }
  };
  const emailPayLink = async (fee) => {
    try { await api.post(`/fees/${fee.id}/send-link`); toast.success("Payment link emailed to parent"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const feeStatus = (f) => { if (f.status === "refunded") return "refunded"; if (f.status === "partially_refunded") return "partial"; const today = new Date().toISOString().slice(0, 10); return f.status === "paid" ? "paid" : (f.overdue || (f.due_date && f.due_date < today)) ? "overdue" : "pending"; };

  const cancelSchedule = async (sid) => { await api.delete(`/fee-schedules/${sid}`); load(); };

  const createTournament = async () => {
    if (!trnForm.name || !trnForm.fee) return toast.error("Name and fee are required");
    try {
      await api.post("/tournaments", { ...trnForm, fee: +trnForm.fee });
      toast.success("Tournament created");
      setTrnOpen(false); setTrnForm({ name: "", date: "", location: "", description: "", fee: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delTournament = async (tid) => { await api.delete(`/tournaments/${tid}`); load(); };

  const toggleTrn = async (tid) => {
    if (expandedTrn === tid) return setExpandedTrn("");
    setExpandedTrn(tid);
    if (!trnRegs[tid]) {
      try { const { data } = await api.get(`/tournaments/${tid}/registrations`); setTrnRegs((m) => ({ ...m, [tid]: data })); }
      catch (e) { console.warn("Could not load tournament registrations", e); }
    }
  };
  const onProdImage = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader(); reader.onload = () => setProdForm((f) => ({ ...f, image: reader.result })); reader.readAsDataURL(file);
  };
  const createProduct = async () => {
    if (!prodForm.name || !prodForm.price) return toast.error("Name and price are required");
    const sizes = prodForm.sizes.split(",").map((s) => s.trim()).filter(Boolean);
    try {
      await api.post("/store/products", { ...prodForm, price: +prodForm.price, sizes });
      toast.success("Product added");
      setStoreOpen(false); setProdForm({ name: "", description: "", price: "", image: "", sizes: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delProduct = async (pid) => { await api.delete(`/store/products/${pid}`); load(); };
  const fulfillOrder = async (o) => {
    const { data } = await api.put(`/store/orders/${o.id}/fulfill`, { fulfilled: !o.fulfilled });
    setOrders((list) => list.map((x) => (x.id === o.id ? { ...x, fulfilled: data.fulfilled } : x)));
  };
  const exportOrders = async () => {
    try {
      const res = await api.get("/store/orders/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "store_orders.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const cur = club?.currency || "USD";
  const totalDue = fees.filter((f) => !["paid", "refunded", "partially_refunded"].includes(f.status)).reduce((a, f) => a + (f.amount || 0), 0);
  const collected = txns.filter((t) => t.payment_status === "paid").reduce((a, t) => a + (t.amount || 0), 0);
  const playerName = (id) => players.find((p) => p.id === id)?.name || "—";

  if (!plans.length) return <Loader />;

  return (
    <div className="animate-fade-up">
      <PageHeader title="Payments" subtitle={`Current plan: ${club?.plan?.toUpperCase()} · ${club?.subscription_active ? "Active" : "Free"}`} testid="payments-header" />

      <Tabs defaultValue="subscription">
        <TabsList data-testid="payments-tabs" className="w-full max-w-full justify-start overflow-x-auto [&::-webkit-scrollbar]:hidden">
          <TabsTrigger value="subscription">Subscription</TabsTrigger>
          <TabsTrigger value="fees">Collect Fees</TabsTrigger>
          <TabsTrigger value="tournaments">Tournaments</TabsTrigger>
          <TabsTrigger value="store">Store</TabsTrigger>
          <TabsTrigger value="finance">Financials</TabsTrigger>
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
        </TabsList>

        <TabsContent value="subscription" className="mt-5">
          {club?.subscription_active && club?.plan !== "free" && (
            <div className="rounded-2xl border border-border bg-card p-5 mb-4 flex items-center justify-between gap-4 flex-wrap" data-testid="subscription-manage">
              <div>
                <p className="font-semibold">You're on the {club.plan?.toUpperCase()} plan.</p>
                {club.subscription_cancel_at
                  ? <p className="text-sm text-amber-500 mt-0.5" data-testid="subscription-cancel-notice">Cancels on {club.subscription_cancel_at.slice(0, 10)} — you keep access until then.</p>
                  : <p className="text-sm text-muted-foreground mt-0.5">Renews monthly · all your plan features are active.</p>}
              </div>
              {club.subscription_cancel_at && (
                <Button variant="outline" size="sm" data-testid="resume-subscription-btn" onClick={async () => {
                  try { await api.post("/payments/subscription/resume"); toast.success("Subscription resumed"); load(); }
                  catch (e) { toast.error(apiErr(e)); }
                }}>Resume subscription</Button>
              )}
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.map((p) => {
              const active = club?.plan === p.key;
              return (
                <div key={p.key} className={`rounded-2xl border p-6 flex flex-col ${p.key === "pro" ? "border-primary" : "border-border"} bg-card`} data-testid={`sub-plan-${p.key}`}>
                  <h3 className="font-heading font-bold text-xl">{p.name}</h3>
                  <p className="mt-2"><span className="font-heading font-extrabold text-3xl">${p.price}</span><span className="text-muted-foreground text-sm">/mo</span></p>
                  {p.price > 0 && <p className="text-xs font-semibold text-secondary mt-1" data-testid={`first-month-deal-${p.key}`}>50% off first month — just ${((Math.round(p.price * 100) - Math.round(Math.round(p.price * 100) / 2)) / 100).toFixed(2)}</p>}
                  <ul className="mt-4 space-y-2 flex-1">
                    {p.features.map((f) => <li key={f} className="flex items-start gap-2 text-sm"><Check size={15} className="text-secondary mt-0.5 shrink-0" /> {f}</li>)}
                  </ul>
                  {active ? (
                    <Button disabled className="mt-5" variant="outline" data-testid={`current-plan-${p.key}`}>Current plan</Button>
                  ) : p.lookup_key ? (
                    <Button onClick={() => subscribe(p.lookup_key)} disabled={busy === p.lookup_key} className="mt-5 brand-gradient text-white border-0 hover:opacity-90" data-testid={`subscribe-${p.key}`}>
                      {busy === p.lookup_key ? "Redirecting..." : "Upgrade"}
                    </Button>
                  ) : (
                    <Button disabled className="mt-5" variant="outline">Free</Button>
                  )}
                </div>
              );
            })}
          </div>
          {club?.subscription_active && club?.plan !== "free" && !club?.subscription_cancel_at && (
            <div className="mt-8 pt-5 border-t border-border text-center">
              <button data-testid="cancel-subscription-btn" onClick={async () => {
                if (!window.confirm("Cancel your subscription? You'll keep access until the end of the current billing period.")) return;
                try { const r = await api.post("/payments/subscription/cancel"); toast.success(`Subscription will cancel on ${r.data.cancel_at?.slice(0, 10) || "period end"}`); load(); }
                catch (e) { toast.error(apiErr(e)); }
              }} className="text-xs text-muted-foreground/70 hover:text-destructive transition-colors underline underline-offset-2">Cancel subscription</button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="fees" className="mt-5">
          {!isPremium ? <UpgradeGate feature="Fee collection" desc="Charge athletes, send Pay-now links, track paid/overdue fees and see revenue. Upgrade to Pro or Club to start collecting money online." testid="fees-upgrade-gate" /> : (<>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <StatCard icon={DollarSign} label="Outstanding" value={`${cur} ${totalDue.toFixed(2)}`} testid="stat-outstanding" />
            <StatCard icon={DollarSign} label="Collected (gross)" value={`${cur} ${collected.toFixed(2)}`} accent="text-secondary" testid="stat-collected" />
          </div>
          {summary?.connected && (
            <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 mb-4" data-testid="fee-breakdown-card">
              <h3 className="font-heading font-semibold text-lg mb-1">Where your money went</h3>
              <p className="text-xs text-muted-foreground mb-4">Families pay the gross amount. Card processing &amp; platform fees are deducted, so your account receives a little less than "collected".</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Collected (gross — families paid)</span><span className="font-medium" data-testid="bd-gross">{cur} {(summary.gross_collected || 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Processing &amp; platform fees</span><span className="font-medium text-destructive" data-testid="bd-fees">− {cur} {(summary.fees_deducted || 0).toFixed(2)}</span></div>
                <div className="flex justify-between border-t border-border pt-2"><span className="font-semibold">Received into your account</span><span className="font-heading font-bold text-secondary" data-testid="bd-received">{cur} {(summary.received_total || 0).toFixed(2)}</span></div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-4">
                <div className="rounded-xl border border-border p-3"><p className="text-[11px] text-muted-foreground">Available now</p><p className="font-heading font-bold" data-testid="bd-available">{cur} {(summary.available || 0).toFixed(2)}</p></div>
                <div className="rounded-xl border border-border p-3"><p className="text-[11px] text-muted-foreground">Pending (clearing)</p><p className="font-heading font-bold" data-testid="bd-pending">{cur} {(summary.pending || 0).toFixed(2)}</p></div>
                <div className="rounded-xl border border-border p-3"><p className="text-[11px] text-muted-foreground">Paid to your bank</p><p className="font-heading font-bold" data-testid="bd-paidout">{cur} {(summary.paid_out || 0).toFixed(2)}</p></div>
              </div>
            </div>
          )}

          <SectionCard title="Club Payouts" className="mb-4" testid="connect-card">
            {!connect?.payouts_enabled ? (
              <>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-xl bg-accent flex items-center justify-center shrink-0"><Landmark size={18} className="text-primary" /></div>
                    <div>
                      <p className="font-medium text-sm">Connect your bank to receive fees</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Collected fees route to your club's Stripe account with no platform fee — only Stripe's processing fee applies.</p>
                    </div>
                  </div>
                  <Button size="sm" onClick={onboardPayouts} disabled={connecting} className="w-full sm:w-auto brand-gradient text-white border-0 hover:opacity-90" data-testid="connect-payouts-btn">
                    {connecting ? "Opening Stripe..." : connect?.connected ? "Finish bank setup" : "Connect your bank"}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-3" data-testid="payouts-help">Clicking "Connect your bank" opens Stripe's secure setup to create your club's payout account (business name, address &amp; bank details) — about 2 minutes. Once approved, collected fees pay out to your bank automatically.</p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-1.5 text-sm font-medium text-secondary mb-4"><CheckCircle2 size={15} /> Payouts connected</div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="rounded-xl border border-border p-4" data-testid="balance-available">
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Wallet size={13} /> Available to cash out</p>
                    <p className="font-heading font-extrabold text-2xl sm:text-3xl mt-2 text-secondary">{balance?.currency || cur} {(balance?.available || 0).toFixed(2)}</p>
                  </div>
                  <div className="rounded-xl border border-border p-4" data-testid="balance-pending">
                    <p className="text-xs text-muted-foreground">On the way (pending)</p>
                    <p className="font-heading font-extrabold text-2xl sm:text-3xl mt-2">{balance?.currency || cur} {(balance?.pending || 0).toFixed(2)}</p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button size="sm" onClick={openPayout} disabled={!balance || balance.available <= 0} className="w-full sm:w-auto brand-gradient text-white border-0 hover:opacity-90" data-testid="cash-out-btn"><ArrowUpRight size={15} className="mr-1.5" /> Cash out</Button>
                  <Button size="sm" variant="outline" onClick={openDashboard} disabled={openingDash} className="w-full sm:w-auto" data-testid="manage-payouts-btn"><ExternalLink size={15} className="mr-1.5" /> {openingDash ? "Opening..." : "Manage in Stripe"}</Button>
                </div>
                <p className="text-xs text-muted-foreground mt-3">Cash out sends your available balance to your bank (usually 1–3 business days). Pending funds from recent payments clear automatically. Use "Manage in Stripe" to set an automatic payout schedule or update bank details.</p>
              </>
            )}
          </SectionCard>

          <Dialog open={payoutOpen} onOpenChange={setPayoutOpen}>
            <DialogContent className="w-[calc(100%-1.5rem)] max-w-md rounded-2xl" data-testid="payout-dialog">
              <DialogHeader><DialogTitle>Cash out to your bank</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">Available balance: <b className="text-foreground">{balance?.currency || cur} {(balance?.available || 0).toFixed(2)}</b>. Payouts usually arrive in 1–3 business days.</p>
                <div>
                  <Label>Amount to cash out ({balance?.currency || cur})</Label>
                  <Input type="number" step="0.01" min="0" value={payoutAmt} onChange={(e) => setPayoutAmt(e.target.value)} className="mt-1.5 h-11" data-testid="payout-amount-input" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPayoutOpen(false)} data-testid="payout-cancel-btn">Cancel</Button>
                <Button onClick={doPayout} disabled={payingOut} className="brand-gradient text-white border-0" data-testid="payout-confirm-btn">{payingOut ? "Sending..." : "Cash out now"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <SectionCard title="Fees Due" testid="fees-section"
            action={
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" data-testid="charge-all-overdue-btn" onClick={chargeAllOverdue}><CreditCard size={15} className="mr-1.5" /> Charge all overdue</Button>
                <Button size="sm" variant="outline" data-testid="remind-overdue-btn" onClick={async () => {
                  try { const r = await api.post("/fees/remind-overdue"); toast.success(`Reminded ${r.data.reminded} famil${r.data.reminded === 1 ? "y" : "ies"} with overdue fees`); }
                  catch (e) { toast.error(apiErr(e)); }
                }}><Bell size={15} className="mr-1.5" /> Remind overdue</Button>
                <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
                  <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="charge-athletes-btn"><Plus size={15} className="mr-1.5" /> Charge athletes</Button></DialogTrigger>
                  <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="bulk-fee-dialog">
                    <DialogHeader><DialogTitle>Charge multiple athletes</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-2">
                      <div>
                        <Label>Athletes ({bulkForm.player_ids.length} selected)</Label>
                        <div className="mt-1.5 max-h-48 overflow-y-auto rounded-lg border border-border divide-y divide-border">
                          {players.length === 0 ? <p className="text-sm text-muted-foreground p-3">No athletes yet.</p> : (
                            <>
                              <label className="flex items-center gap-2 p-2.5 text-sm cursor-pointer bg-accent/40">
                                <Checkbox checked={bulkForm.player_ids.length === players.length && players.length > 0} onCheckedChange={(v) => setBulkForm((f) => ({ ...f, player_ids: v ? players.map((p) => p.id) : [] }))} data-testid="bulk-select-all" />
                                Select all
                              </label>
                              {players.map((p) => (
                                <label key={p.id} className="flex items-center gap-2 p-2.5 text-sm cursor-pointer" data-testid={`bulk-player-${p.id}`}>
                                  <Checkbox checked={bulkForm.player_ids.includes(p.id)} onCheckedChange={() => toggleBulkPlayer(p.id)} />
                                  {p.name}
                                </label>
                              ))}
                            </>
                          )}
                        </div>
                      </div>
                      <div><Label>Fee type</Label>
                        <Select value={bulkForm.fee_type} onValueChange={(v) => setBulkForm({ ...bulkForm, fee_type: v })}>
                          <SelectTrigger className="mt-1.5" data-testid="bulk-fee-type"><SelectValue /></SelectTrigger>
                          <SelectContent>{FEE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>Amount ({cur})</Label><Input type="number" value={bulkForm.amount} onChange={(e) => setBulkForm({ ...bulkForm, amount: e.target.value })} className="mt-1.5" data-testid="bulk-amount" /></div>
                        <div><Label>Due date</Label><Input type="date" value={bulkForm.due_date} onChange={(e) => setBulkForm({ ...bulkForm, due_date: e.target.value })} className="mt-1.5" /></div>
                      </div>
                      <div><Label>Description (optional)</Label><Input value={bulkForm.description} onChange={(e) => setBulkForm({ ...bulkForm, description: e.target.value })} className="mt-1.5" data-testid="bulk-description" /></div>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={bulkForm.send_links} onCheckedChange={(v) => setBulkForm({ ...bulkForm, send_links: !!v })} data-testid="bulk-send-links" />
                        <Mail size={14} /> Email each parent a "Pay now" link
                      </label>
                    </div>
                    <DialogFooter><Button onClick={createBulkFees} className="brand-gradient text-white border-0" data-testid="save-bulk-fee-btn">{`Charge ${bulkForm.player_ids.length || ""} athlete${bulkForm.player_ids.length === 1 ? "" : "s"}`}</Button></DialogFooter>
                  </DialogContent>
                </Dialog>
                <Dialog open={feeOpen} onOpenChange={setFeeOpen}>
                  <DialogTrigger asChild><Button size="sm" className="brand-gradient text-white border-0" data-testid="add-fee-btn"><Plus size={15} className="mr-1.5" /> New fee</Button></DialogTrigger>
                  <DialogContent className="w-[calc(100%-1.5rem)] max-w-lg rounded-2xl" data-testid="fee-dialog">
                    <DialogHeader><DialogTitle>Create fee</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-2">
                      <div><Label>Athlete</Label>
                        <Select value={feeForm.player_id} onValueChange={(v) => setFeeForm({ ...feeForm, player_id: v })}>
                          <SelectTrigger className="mt-1.5 h-11" data-testid="fee-player-select"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{players.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label>Fee type</Label>
                        <Select value={feeForm.fee_type} onValueChange={(v) => setFeeForm({ ...feeForm, fee_type: v })}>
                          <SelectTrigger className="mt-1.5 h-11" data-testid="fee-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{FEE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div><Label>Amount ({cur})</Label><Input type="number" value={feeForm.amount} onChange={(e) => setFeeForm({ ...feeForm, amount: e.target.value })} className="mt-1.5 h-11" data-testid="fee-amount-input" /></div>
                        <div><Label>{recurring ? "Bill day of month" : "Due date"}</Label>
                          {recurring
                            ? <Input type="number" min="1" max="28" value={dayOfMonth} onChange={(e) => setDayOfMonth(e.target.value)} className="mt-1.5 h-11" data-testid="fee-day-input" />
                            : <Input type="date" value={feeForm.due_date} onChange={(e) => setFeeForm({ ...feeForm, due_date: e.target.value })} className="mt-1.5 h-11" />}
                        </div>
                      </div>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={recurring} onCheckedChange={(v) => setRecurring(!!v)} data-testid="fee-recurring-checkbox" />
                        <Repeat size={14} /> Bill this automatically every month
                      </label>
                    </div>
                    <DialogFooter><Button onClick={createFee} className="brand-gradient text-white border-0" data-testid="save-fee-btn">{recurring ? "Set up recurring" : "Create"}</Button></DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            }>
            <div className="flex items-center gap-1.5 mb-4" data-testid="fee-filters">
              {["all", "pending", "overdue", "paid"].map((s) => (
                <button key={s} onClick={() => setFeeFilter(s)} data-testid={`fee-filter-${s}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors duration-150 ${feeFilter === s ? "brand-gradient text-white" : "bg-accent text-muted-foreground hover:text-foreground"}`}>{s}</button>
              ))}
            </div>
            {(() => {
              const shown = fees.filter((f) => feeFilter === "all" || feeStatus(f) === feeFilter);
              return shown.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No {feeFilter === "all" ? "" : feeFilter + " "}fees{feeFilter === "all" ? " created yet." : "."}</p> : (
                <div className="divide-y divide-border">
                  {shown.map((f) => {
                    const st = feeStatus(f);
                    return (
                      <div key={f.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 py-3" data-testid={`fee-row-${f.id}`}>
                        <div>
                          <p className="font-medium text-sm flex items-center gap-2 flex-wrap">{playerName(f.player_id)} · {f.fee_type}
                            {st === "overdue" && <span className="text-[10px] uppercase tracking-wide bg-destructive/15 text-destructive px-1.5 py-0.5 rounded" data-testid={`fee-overdue-${f.id}`}>Overdue</span>}
                            {st === "paid" && <span className="text-[10px] uppercase tracking-wide bg-secondary/15 text-secondary px-1.5 py-0.5 rounded" data-testid={`fee-paid-${f.id}`}>Paid</span>}
                            {st === "pending" && <span className="text-[10px] uppercase tracking-wide bg-amber-400/15 text-amber-500 px-1.5 py-0.5 rounded" data-testid={`fee-pending-${f.id}`}>Pending</span>}
                            {st === "refunded" && <span className="text-[10px] uppercase tracking-wide bg-blue-400/15 text-blue-400 px-1.5 py-0.5 rounded" data-testid={`fee-refunded-${f.id}`}>Refunded</span>}
                            {st === "partial" && <span className="text-[10px] uppercase tracking-wide bg-blue-400/15 text-blue-400 px-1.5 py-0.5 rounded" data-testid={`fee-partial-refund-${f.id}`}>Partial refund</span>}
                          </p>
                          <p className="text-xs text-muted-foreground">Due {f.due_date || "—"}{f.link_sent_at ? " · link sent" : ""}{f.refunded_amount ? ` · ${cur} ${f.refunded_amount.toFixed(2)} refunded` : ""}</p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                          <span className="font-heading font-bold">{cur} {f.amount?.toFixed(2)}</span>
                          {(st === "paid" || st === "partial") && (
                            <Button size="sm" variant="outline" onClick={() => openRefund(f)} data-testid={`refund-fee-${f.id}`}><Undo2 size={13} className="mr-1.5" /> Refund</Button>
                          )}
                          {st !== "paid" && st !== "refunded" && st !== "partial" && (
                            <>
                              <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${hasCard(f.player_id) ? "bg-secondary/15 text-secondary" : "bg-accent text-muted-foreground"}`} data-testid={`fee-card-badge-${f.id}`} title={hasCard(f.player_id) ? "Saved card on file — Charge now will work" : "No saved card — send a payment link instead"}>
                                <CreditCard size={11} /> {hasCard(f.player_id) ? "Card" : "No card"}
                              </span>
                              <Button size="sm" onClick={() => chargeNow(f)} className="brand-gradient text-white border-0" data-testid={`charge-now-${f.id}`}><CreditCard size={13} className="mr-1.5" /> Charge now</Button>
                              <Button size="sm" variant="outline" onClick={() => copyPayLink(f)} data-testid={`copy-link-${f.id}`}><LinkIcon size={13} className="mr-1.5" /> Copy</Button>
                              <Button size="sm" variant="outline" onClick={() => emailPayLink(f)} data-testid={`email-link-${f.id}`}><Mail size={13} className="mr-1.5" /> Email</Button>
                              <Button size="sm" variant="outline" onClick={() => remind(f.id)} data-testid={`remind-fee-${f.id}`}><Bell size={13} className="mr-1.5" /> Remind</Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </SectionCard>

          <Dialog open={!!refundFee} onOpenChange={(o) => !o && setRefundFee(null)}>
            <DialogContent data-testid="refund-dialog">
              <DialogHeader><DialogTitle>Refund fee</DialogTitle></DialogHeader>
              {refundFee && (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Refund <b className="text-foreground">{playerName(refundFee.player_id)}</b> for <b className="text-foreground">{refundFee.fee_type}</b>. Original charge {cur} {refundFee.amount?.toFixed(2)}
                    {refundFee.refunded_amount ? ` · ${cur} ${refundFee.refunded_amount.toFixed(2)} already refunded` : ""}. The money is returned to the original payment method via Stripe.
                  </p>
                  <div>
                    <Label>Refund amount ({cur})</Label>
                    <Input type="number" step="0.01" min="0" value={refundAmt} onChange={(e) => setRefundAmt(e.target.value)} className="mt-1.5 h-11" data-testid="refund-amount-input" />
                    <p className="text-xs text-muted-foreground mt-1.5">Enter a smaller amount for a partial refund (e.g. an overcharge), or leave the full amount for a duplicate charge.</p>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setRefundFee(null)} data-testid="refund-cancel-btn">Cancel</Button>
                <Button onClick={doRefund} disabled={refunding} className="brand-gradient text-white border-0" data-testid="refund-confirm-btn">{refunding ? "Refunding..." : "Issue refund"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

            {schedules.length > 0 && (
            <SectionCard title="Recurring monthly fees" className="mt-4" testid="schedules-section">
              <div className="divide-y divide-border">
                {schedules.map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-3 py-3" data-testid={`schedule-${s.id}`}>
                    <div className="flex items-center gap-2"><Repeat size={15} className="text-secondary" /><div><p className="font-medium text-sm">{playerName(s.player_id)} · {s.fee_type}</p><p className="text-xs text-muted-foreground">{cur} {s.amount?.toFixed(2)} on day {s.day_of_month} each month</p></div></div>
                    <button onClick={() => cancelSchedule(s.id)} className="text-muted-foreground hover:text-destructive" data-testid={`cancel-schedule-${s.id}`}><X size={16} /></button>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
          <p className="text-xs text-muted-foreground mt-4">Parents pay fees online via your public club registration link. SphereHyx charges no platform fee — only Stripe's processing fee applies, covered by the payer.</p>
          </>)}
        </TabsContent>

        <TabsContent value="tournaments" className="mt-5">
          <SectionCard title="Tournaments & Events" testid="tournaments-section"
            action={
              <Dialog open={trnOpen} onOpenChange={setTrnOpen}>
                <DialogTrigger asChild><Button size="sm" className="brand-gradient text-white border-0" data-testid="add-tournament-btn"><Plus size={15} className="mr-1.5" /> New tournament</Button></DialogTrigger>
                <DialogContent data-testid="tournament-dialog">
                  <DialogHeader><DialogTitle>Create tournament</DialogTitle></DialogHeader>
                  <div className="space-y-4 py-2">
                    <div><Label>Name</Label><Input value={trnForm.name} onChange={(e) => setTrnForm({ ...trnForm, name: e.target.value })} placeholder="Spring Cup 2026" className="mt-1.5" data-testid="trn-name-input" /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Date</Label><Input type="date" value={trnForm.date} onChange={(e) => setTrnForm({ ...trnForm, date: e.target.value })} className="mt-1.5" data-testid="trn-date-input" /></div>
                      <div><Label>Fee ({cur})</Label><Input type="number" value={trnForm.fee} onChange={(e) => setTrnForm({ ...trnForm, fee: e.target.value })} className="mt-1.5" data-testid="trn-fee-input" /></div>
                    </div>
                    <div><Label>Location</Label><Input value={trnForm.location} onChange={(e) => setTrnForm({ ...trnForm, location: e.target.value })} className="mt-1.5" data-testid="trn-location-input" /></div>
                    <div><Label>Description</Label><Input value={trnForm.description} onChange={(e) => setTrnForm({ ...trnForm, description: e.target.value })} className="mt-1.5" data-testid="trn-desc-input" /></div>
                  </div>
                  <DialogFooter><Button onClick={createTournament} className="brand-gradient text-white border-0" data-testid="save-tournament-btn">Create</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            }>
            {tournaments.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No tournaments yet. Create one so parents can register and pay from their portal.</p> : (
              <div className="divide-y divide-border">
                {tournaments.map((t) => (
                  <div key={t.id} className="py-3" data-testid={`tournament-${t.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-medium text-sm">{t.name}</p>
                        <p className="text-xs text-muted-foreground">{t.date || "Date TBD"}{t.location ? ` · ${t.location}` : ""} · {t.registrations || 0} registered</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-heading font-bold">{cur} {t.fee?.toFixed(2)}</span>
                        <Button size="sm" variant="outline" onClick={() => toggleTrn(t.id)} data-testid={`view-roster-${t.id}`}>{expandedTrn === t.id ? "Hide" : "Roster"}</Button>
                        <button onClick={() => delTournament(t.id)} className="text-muted-foreground hover:text-destructive" data-testid={`del-tournament-${t.id}`}><X size={16} /></button>
                      </div>
                    </div>
                    {expandedTrn === t.id && (
                      <div className="mt-3 pl-1 space-y-1.5" data-testid={`roster-${t.id}`}>
                        {(trnRegs[t.id] || []).length === 0
                          ? <p className="text-xs text-muted-foreground">No paid registrations yet.</p>
                          : (trnRegs[t.id] || []).map((r) => (
                            <div key={r.player_id} className="flex items-center gap-2 text-sm"><CheckCircle2 size={14} className="text-secondary" /> {r.player_name}</div>
                          ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
          <p className="text-xs text-muted-foreground mt-4">Parents register and pay per child from their portal. SphereHyx charges no platform fee — only Stripe's processing fee applies, covered by the payer.</p>
        </TabsContent>

        <TabsContent value="store" className="mt-5">
          {!isPremium ? <UpgradeGate feature="Online store" desc="Sell club merchandise and kit online and track orders. Upgrade to Pro or Club to open your store." testid="store-upgrade-gate" /> : (<>
          <SectionCard title="Merchandise Store" testid="store-section"
            action={
              <Dialog open={storeOpen} onOpenChange={setStoreOpen}>
                <DialogTrigger asChild><Button size="sm" className="brand-gradient text-white border-0" data-testid="add-product-btn"><Plus size={15} className="mr-1.5" /> New product</Button></DialogTrigger>
                <DialogContent data-testid="product-dialog">
                  <DialogHeader><DialogTitle>Add product</DialogTitle></DialogHeader>
                  <div className="space-y-4 py-2">
                    <div className="flex items-center gap-3">
                      {prodForm.image && <img src={prodForm.image} alt="" className="h-14 w-14 rounded-lg object-cover border border-border" />}
                      <div className="flex-1"><Label>Image</Label><Input type="file" accept="image/*" onChange={onProdImage} className="mt-1.5" data-testid="prod-image-input" /></div>
                    </div>
                    <div><Label>Name</Label><Input value={prodForm.name} onChange={(e) => setProdForm({ ...prodForm, name: e.target.value })} placeholder="Home Jersey" className="mt-1.5" data-testid="prod-name-input" /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Price ({cur})</Label><Input type="number" value={prodForm.price} onChange={(e) => setProdForm({ ...prodForm, price: e.target.value })} className="mt-1.5" data-testid="prod-price-input" /></div>
                      <div><Label>Sizes (comma-sep)</Label><Input value={prodForm.sizes} onChange={(e) => setProdForm({ ...prodForm, sizes: e.target.value })} placeholder="S, M, L" className="mt-1.5" data-testid="prod-sizes-input" /></div>
                    </div>
                    <div><Label>Description</Label><Input value={prodForm.description} onChange={(e) => setProdForm({ ...prodForm, description: e.target.value })} className="mt-1.5" data-testid="prod-desc-input" /></div>
                  </div>
                  <DialogFooter><Button onClick={createProduct} className="brand-gradient text-white border-0" data-testid="save-product-btn">Add product</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            }>
            {products.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No products yet. Add uniforms or gear for parents to buy.</p> : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {products.map((p) => (
                  <div key={p.id} className="rounded-xl border border-border overflow-hidden" data-testid={`product-${p.id}`}>
                    {p.image ? <img src={p.image} alt="" className="h-36 w-full object-cover" /> : <div className="h-36 w-full bg-accent flex items-center justify-center text-muted-foreground text-sm">No image</div>}
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div><p className="font-medium text-sm">{p.name}</p><p className="text-xs text-muted-foreground">{cur} {p.price?.toFixed(2)} · {p.orders || 0} sold</p></div>
                        <button onClick={() => delProduct(p.id)} className="text-muted-foreground hover:text-destructive" data-testid={`del-product-${p.id}`}><X size={16} /></button>
                      </div>
                      {p.sizes?.length > 0 && <p className="text-xs text-muted-foreground mt-2">Sizes: {p.sizes.join(", ")}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
          <SectionCard title="Orders" className="mt-4" testid="orders-section"
            action={
              <div className="flex flex-wrap items-center gap-1.5">
                {["all", "unfulfilled", "fulfilled"].map((f) => (
                  <button key={f} onClick={() => setOrderFilter(f)} data-testid={`order-filter-${f}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors duration-150 ${orderFilter === f ? "brand-gradient text-white" : "bg-accent text-muted-foreground hover:text-foreground"}`}>{f}</button>
                ))}
                <button onClick={exportOrders} className="ml-1 px-3 py-1.5 rounded-full text-xs font-medium bg-accent text-muted-foreground hover:text-foreground" data-testid="export-orders-btn">Export CSV</button>
              </div>
            }>
            {(() => {
              const shown = orders.filter((o) => orderFilter === "all" || (orderFilter === "fulfilled" ? o.fulfilled : !o.fulfilled));
              return shown.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No {orderFilter === "all" ? "" : orderFilter + " "}orders{orderFilter === "all" ? " yet. Paid purchases from parents will appear here." : "."}</p> : (
              <div className="divide-y divide-border">
                {shown.map((o) => (
                  <div key={o.id} className="flex items-center justify-between gap-3 py-3" data-testid={`order-${o.id}`}>
                    <div>
                      <p className="font-medium text-sm">{o.product_name}{o.size ? ` · ${o.size}` : ""}</p>
                      <p className="text-xs text-muted-foreground">{o.buyer_name || o.buyer_email || "—"} · {o.created_at?.slice(0, 10)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-heading font-bold">{cur} {o.amount?.toFixed(2)}</span>
                      <Button size="sm" variant={o.fulfilled ? "outline" : "default"} onClick={() => fulfillOrder(o)} className={o.fulfilled ? "" : "brand-gradient text-white border-0"} data-testid={`fulfill-order-${o.id}`}>
                        {o.fulfilled ? <><CheckCircle2 size={13} className="mr-1.5 text-secondary" /> Fulfilled</> : "Mark fulfilled"}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ); })()}
          </SectionCard>
          <p className="text-xs text-muted-foreground mt-4">Parents buy from their portal; payments use your Stripe setup with no platform fee (Stripe's processing fee is covered by the payer).</p>
          </>)}
        </TabsContent>

        <TabsContent value="finance" className="mt-5">
          {breakdown?.savings && (breakdown.savings.bank_count > 0 || breakdown.savings.card_count > 0) && (
            <div className="rounded-2xl border border-secondary/40 bg-secondary/5 p-4 sm:p-5 mb-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="bank-savings-card">
              <div className="flex-1">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Bank debit savings · {breakdown.savings.season} season</p>
                <p className="font-heading font-extrabold text-2xl text-secondary mt-1" data-testid="bank-savings-amount">{breakdown.currency} {breakdown.savings.saved.toFixed(2)} saved</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {breakdown.savings.bank_count} of {breakdown.savings.bank_count + breakdown.savings.card_count} payments came by bank debit ({breakdown.currency} {breakdown.savings.bank_volume.toFixed(2)}), each costing ~0.8% instead of 2.9% + 0.30 on card.
                </p>
              </div>
              {breakdown.savings.potential > 0 && (
                <div className="rounded-xl border border-border bg-card p-3 sm:w-64" data-testid="bank-savings-potential">
                  <p className="text-[11px] text-muted-foreground">If every card payment had used bank debit</p>
                  <p className="font-heading font-bold text-lg">+ {breakdown.currency} {breakdown.savings.potential.toFixed(2)}</p>
                  {!breakdown.savings.bank_debit_enabled && <p className="text-[11px] text-primary mt-1">Turn on bank debit in Settings to unlock this.</p>}
                </div>
              )}
            </div>
          )}
          {breakdown?.items?.length > 0 && (
            <SectionCard title="Fee breakdown — where every dollar went" className="mb-4" testid="fee-breakdown-detail">
              <p className="text-xs text-muted-foreground mb-4">SphereHyx charges <b>no platform fee</b>. Each payment now carries only <b>Stripe's own processing fee</b> (about 2.9% + a fixed charge on cards, ~0.8% capped on bank debit), which is covered by the payer — so your club receives the full amount. Older payments below may still show a legacy platform fee.</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                <div className="rounded-xl border border-border p-3" data-testid="bd-total-gross"><p className="text-[11px] text-muted-foreground">Families paid (gross)</p><p className="font-heading font-bold text-lg">{breakdown.currency} {breakdown.totals.gross.toFixed(2)}</p></div>
                <div className="rounded-xl border border-border p-3" data-testid="bd-total-sphere"><p className="text-[11px] text-muted-foreground">SphereHyx fee</p><p className="font-heading font-bold text-lg text-destructive">− {breakdown.currency} {breakdown.totals.sphere_fee.toFixed(2)}</p></div>
                <div className="rounded-xl border border-border p-3" data-testid="bd-total-stripe"><p className="text-[11px] text-muted-foreground">Stripe processing</p><p className="font-heading font-bold text-lg text-destructive">− {breakdown.currency} {breakdown.totals.stripe_fee.toFixed(2)}</p></div>
                <div className="rounded-xl border border-border p-3 bg-secondary/5" data-testid="bd-total-net"><p className="text-[11px] text-muted-foreground">Net to your club</p><p className="font-heading font-bold text-lg text-secondary">{breakdown.currency} {breakdown.totals.net.toFixed(2)}</p></div>
              </div>
              <div className="overflow-x-auto -mx-1 px-1">
                <table className="w-full text-sm min-w-[560px]" data-testid="fee-breakdown-table">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border">
                      <th className="py-2 pr-3 font-medium">Date</th>
                      <th className="py-2 pr-3 font-medium">Item</th>
                      <th className="py-2 pr-3 font-medium text-right">Gross</th>
                      <th className="py-2 pr-3 font-medium text-right">SphereHyx fee</th>
                      <th className="py-2 pr-3 font-medium text-right">Stripe fee</th>
                      <th className="py-2 font-medium text-right">Net to club</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {breakdown.items.map((it, i) => (
                      <tr key={it.id || i} data-testid={`bd-row-${i}`}>
                        <td className="py-2.5 pr-3 text-muted-foreground whitespace-nowrap">{it.date || "—"}</td>
                        <td className="py-2.5 pr-3">{it.player_id ? `${playerName(it.player_id)} · ` : ""}{it.item}</td>
                        <td className="py-2.5 pr-3 text-right font-medium whitespace-nowrap">{breakdown.currency} {it.gross.toFixed(2)}</td>
                        <td className="py-2.5 pr-3 text-right text-destructive whitespace-nowrap">− {it.sphere_fee.toFixed(2)}</td>
                        <td className="py-2.5 pr-3 text-right text-destructive whitespace-nowrap">− {it.stripe_fee.toFixed(2)}</td>
                        <td className="py-2.5 text-right font-semibold text-secondary whitespace-nowrap">{breakdown.currency} {it.net.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-muted-foreground mt-3">Stripe deducts its processing fee before funds settle. SphereHyx no longer takes a platform fee — the payer covers Stripe's fee, so your club receives the full amount. Older payments may reflect a legacy platform fee.</p>
            </SectionCard>
          )}
          <SectionCard title="Transactions" testid="transactions-section">
            {txns.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No transactions yet.</p> : (
              <div className="divide-y divide-border">
                {txns.map((t) => (
                  <div key={t.session_id} className="flex items-center justify-between py-3">
                    <div><p className="font-medium text-sm capitalize">{t.fee_type || t.kind}</p><p className="text-xs text-muted-foreground">{t.created_at?.slice(0, 10)}</p></div>
                    <div className="text-right">
                      <p className="font-heading font-bold">{(t.currency || "usd").toUpperCase()} {t.amount?.toFixed(2)}</p>
                      <span className={`text-xs ${t.payment_status === "paid" ? "text-emerald-400" : "text-muted-foreground"}`}>{t.payment_status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="revenue" className="mt-5">
          {!isPremium ? <UpgradeGate feature="Fee collection" desc="See month-by-month collected vs outstanding revenue. Upgrade to Pro or Club to unlock the revenue dashboard." testid="revenue-upgrade-gate" /> : (
          <SectionCard title="Revenue" testid="revenue-section">
            {!revenue ? <p className="text-sm text-muted-foreground py-6 text-center">Loading...</p> : (
              <>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="rounded-xl border border-border p-4" data-testid="revenue-collected">
                    <p className="text-xs text-muted-foreground">Total collected</p>
                    <p className="font-heading font-extrabold text-2xl text-secondary">{cur} {revenue.total_collected?.toFixed(2)}</p>
                  </div>
                  <div className="rounded-xl border border-border p-4" data-testid="revenue-outstanding">
                    <p className="text-xs text-muted-foreground">Outstanding</p>
                    <p className="font-heading font-extrabold text-2xl">{cur} {revenue.total_outstanding?.toFixed(2)}</p>
                  </div>
                </div>
                {revenue.months?.length > 0 && (
                  <div className="h-72 w-full" data-testid="revenue-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={revenue.months} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(m) => (m || "").slice(5)} />
                        <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                        <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }}
                          formatter={(v) => `${cur} ${Number(v).toFixed(2)}`} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="collected" name="Collected" fill="#22c55e" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="outstanding" name="Outstanding" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
            )}
          </SectionCard>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
